/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useCallback, useEffect, useRef } from "react";
import themePropTypes from "../theme-prop-types";
import {
  CLEAR_SPECIALTIES,
  DEFAULT_SPECIALTIES_DATA
} from "../../utils/constants";

const Select = ({
  appData,
  disabled = false,
  name,
  id,
  labelId,
  listId,
  options,
  selectedOption,
  onChange,
  onBlur,
  fieldAttributes = {},
  invalid,
  placeholder = "",
  runFieldAction,
  hideSelectedOptions,
  autoCompleteDisabled
}) => {
  const [selected, setSelected] = useState(
    fieldAttributes.defaultValue || fieldAttributes.value || selectedOption
  );
  const [highlighted, setHighlighted] = useState(-1);
  const [expanded, setExpanded] = useState(false);
  const [focusedItem, setFocusedItem] = useState(null);

  const fieldRef = useRef();
  const listRef = useRef();
  const itemRef = useRef();

  useEffect(() => {
    if (fieldAttributes.defaultValue) {
      setSelected(fieldAttributes.defaultValue);
      onChange(fieldAttributes.defaultValue);
    }
  }, []);

  useEffect(() => {
    if (appData?.values[CLEAR_SPECIALTIES] && isSpecialtyField) {
      setSelected("");
    } else {
      setSelected(selectedOption);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (listRef.current && itemRef.current) {
      setFocusedItem(itemRef.current.id);
      const listRect = listRef.current.getBoundingClientRect();
      const itemRect = itemRef.current.getBoundingClientRect();
      if (itemRect.top < listRect.top) {
        listRef.current.scrollTop += itemRect.top - listRect.top;
      } else if (itemRect.bottom > listRect.bottom) {
        listRef.current.scrollTop += itemRect.bottom - listRect.bottom;
      }
    }
  }, [highlighted]);

  const isSpecialtyField = Object.keys(DEFAULT_SPECIALTIES_DATA).includes(name);

  const optionClassnames = (option, index) => {
    return `custom-select-option-web-sdk${
      index === highlighted ? " highlighted" : ""
    }${!option.value ? " empty-option" : ""}${
      hideSelectedOptions && option.value === selectedOption
        ? " hidden-option"
        : ""
    }`;
  };

  const visibleOptions = hideSelectedOptions
    ? options.filter(option => option.value !== selected)
    : options;

  const selectHighlighted = useCallback(() => {
    if (highlighted >= 0) {
      const newSelected = visibleOptions[highlighted].value;
      setSelected(newSelected);
      setHighlighted(-1);
      setExpanded(false);
      onChange(newSelected);
      if (isSpecialtyField) {
        appData?.handleChange({ [CLEAR_SPECIALTIES]: false });
      }

      if (runFieldAction) {
        const newData = {
          [name]: newSelected
        };

        runFieldAction(newData);
      }
    }
  }, [setSelected, highlighted, onChange]);

  const expand = () => {
    setExpanded(true);
    setHighlighted(visibleOptions.findIndex(o => o.value === selected));
  };

  const handleBlur = e => {
    if (fieldRef.current && !fieldRef.current.contains(e.relatedTarget)) {
      setExpanded(false);
      if (onBlur) {
        onBlur(e);
      }
    }
  };

  const handleKeyDown = useCallback(
    e => {
      switch (e.key) {
        case "ArrowUp":
          if (expanded) {
            setHighlighted(
              highlighted <= 0 ? visibleOptions.length - 1 : highlighted - 1
            );
          } else {
            expand();
          }
          e.preventDefault();
          break;

        case "ArrowDown":
          if (expanded) {
            setHighlighted((highlighted + 1) % visibleOptions.length);
          } else {
            expand();
          }
          e.preventDefault();
          break;

        case " ":
        case "Enter":
          if (expanded) {
            selectHighlighted();
          } else {
            expand();
          }
          e.preventDefault();
          break;

        case "Escape":
          setExpanded(false);
          break;

        default:
      }
    },
    [selected, highlighted, expanded, focusedItem]
  );

  return (
    <div
      className='custom-select-wrapper-web-sdk'
      ref={fieldRef}
      onBlur={handleBlur}
    >
      <input
        data-testid={`custom-select-${name}`}
        id={id || `${name}-input`}
        name={name}
        type='text'
        placeholder={placeholder}
        className={`custom-select-web-sdk${expanded ? " expanded" : ""}${
          invalid ? " invalid" : ""
        }${disabled ? " disabled" : ""}`}
        value={options.find(o => o.value === selected)?.label || ""}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={handleKeyDown}
        readOnly
        disabled={disabled}
        role='combobox'
        aria-labelledby={labelId || `${name}-label`}
        aria-invalid={invalid}
        aria-describedby={invalid ? `inline-error-${name}` : null}
        tabIndex='0'
        aria-haspopup='listbox'
        aria-autocomplete='list'
        aria-controls={listId || `${name}-list`}
        aria-owns={listId || `${name}-list`}
        aria-expanded={expanded}
        aria-activedescendant={focusedItem}
        autoComplete={autoCompleteDisabled ? "off" : "on"}
        aria-readonly
      />
      <ul
        id={listId || `${name}-list`}
        role='listbox'
        tabIndex='-1'
        className={`custom-select-option-list-web-sdk ${
          expanded ? "expanded" : ""
        }`}
        ref={listRef}
        aria-expanded={expanded}
      >
        {visibleOptions.map((option, index) => (
          <li
            data-testid={`${name}-${option.value}`}
            id={`${name}-${option.value}`}
            role='option'
            key={option.value}
            aria-selected={`${name}-${option.value}` === focusedItem}
            className={optionClassnames(option, index)}
            onMouseDown={() => selectHighlighted()}
            onMouseEnter={() => setHighlighted(index)}
            ref={index === highlighted ? itemRef : null}
          >
            {option.label || <>&nbsp;</>}
          </li>
        ))}
      </ul>
    </div>
  );
};

Select.propTypes = themePropTypes.select;

export default Select;
